import { Select, Card, Statistic, Row, Col } from 'antd';
import ConsumerMap from '../../../components/map/ConsumerMap';
import {
  districtBorderStyleFunction,
  getColorByIntensity,
  normalizeString,
} from '../../../utils/defaultMapFunctions';
import { forwardRef, Suspense, useCallback, useEffect, useState } from 'react';
import { ENVIRONMENT_CONFIG } from '../../../constants/environmentConfig';
import { API_ENDPOINTS } from '../../../constants/apiEndpoints';
import { Configuration } from '../../../constants/configs';
import fetcher from '../../../helpers/fetcher';
import {
  CityLocationFeature,
  CityLocationProperties,
  DistrictLocationFeature,
  DistrictLocationProperties,
} from '../../../components/map/iConsumerMap';
import { SpinLoader, SpinTypes } from '../../../components/common/SpinLoader';
import { referenceDataSyncService } from '../../../services/referenceDataSyncService';
import type { Basket } from '../../../types/basket';
import type { Product } from '../../../types/presentation';
import { unwrapDataResponse } from '../../../utils/unwrapDataResponse';

interface UserRegionComparisonProps {
  t: (key: string) => string;
}

interface RegionalCityPrice {
  locationId: number;
  city: string;
  price: number;
  avgPrice?: number;
  inflation?: number;
  inspectionCount: number;
  establishmentCount: number;
}

interface RegionalDistrictPrice {
  district: string;
  price: number;
  avgPrice?: number;
  inflation?: number;
  inspectionCount: number;
  establishmentCount: number;
}

const normalizeRegionalPrice = <
  T extends {
    price?: number;
    avgPrice?: number;
  },
>(
  item: T
): T & { price: number } => ({
  ...item,
  price: Number(item.price ?? item.avgPrice ?? 0),
});

interface UserLocation {
  department: string;
  municipality: string;
  locationId?: number;
}

const UserRegionComparison = forwardRef<HTMLDivElement, UserRegionComparisonProps>(({ t }, ref) => {
  const [basketLoading, setBasketLoading] = useState<boolean>(false);
  const [presentationLoading, setPresentationLoading] = useState<boolean>(false);
  const [priceLoading, setPriceLoading] = useState<boolean>(false);

  const [baskets, setBaskets] = useState<Array<Basket & { productCount: number }>>([]);
  const [selectedBasket, setSelectedBasket] = useState<Basket | undefined>(undefined);

  const [presentations, setPresentations] = useState<Array<Product>>([]);
  const [selectedPresentation, setSelectedPresentation] = useState<Product | undefined>(undefined);

  const [userLocation, setUserLocation] = useState<UserLocation | null>(null);
  const [locations, setLocations] = useState<Array<{ district: string; city: string; id: number }>>(
    []
  );
  const [departments, setDepartments] = useState<string[]>([]);
  const [municipalities, setMunicipalities] = useState<string[]>([]);
  const [selectedDepartment, setSelectedDepartment] = useState<string>(
    Configuration.defaultLocation.department
  );
  const [selectedMunicipality, setSelectedMunicipality] = useState<string>(
    Configuration.defaultLocation.municipality
  );

  const [selectedTimeframe, setSelectedTimeframe] = useState<string>('7d');

  const timeframes = [
    { label: t('last24Hours'), value: '1d' },
    { label: t('last7Days'), value: '7d' },
    { label: t('last30Days'), value: '30d' },
    { label: t('last90Days'), value: '90d' },
  ];

  const [regionalPrice, setRegionalPrice] = useState<Array<RegionalCityPrice> | null>(null);
  const [maxRegionalPrice, setMaxRegionalPrice] = useState(0);

  const [regionalDistrictPrice, setRegionalDistrictPrice] =
    useState<Array<RegionalDistrictPrice> | null>(null);
  const [maxRegionalDistrictPrice, setMaxRegionalDistrictPrice] = useState(0);

  const [departmentAveragePrice, setDepartmentAveragePrice] = useState<number | null>(null);
  const [municipalityAveragePrice, setMunicipalityAveragePrice] = useState<number | null>(null);
  const [nationalAveragePrice, setNationalAveragePrice] = useState<number | null>(null);

  const fetchBasketsData = useCallback(async () => {
    try {
      setBasketLoading(true);
      const basketsData = await referenceDataSyncService.getBaskets();
      setBaskets(basketsData);

      if (!selectedBasket) {
        const basicBasket = await referenceDataSyncService.getBasicFoodBasket();
        if (basicBasket) {
          setSelectedBasket(basicBasket);
        }
      }
    } catch (error) {
      console.error('Failed to load baskets from IndexedDB:', error);
    } finally {
      setBasketLoading(false);
    }
  }, [selectedBasket]);

  const fetchLocationsData = useCallback(async () => {
    try {
      const locationsData = await referenceDataSyncService.getLocations();
      const formattedLocations = locationsData.map((location: Record<string, unknown>) => ({
        district: location.district as string,
        city: location.city as string,
        id: location.id as number,
      }));
      setLocations(formattedLocations);

      const uniqueDepartments = Array.from(
        new Set(formattedLocations.map((location) => location.district))
      );
      setDepartments(uniqueDepartments);
    } catch (error) {
      console.error('Failed to load locations from IndexedDB:', error);
    }
  }, []);

  const fetchPresentationsData = useCallback(async () => {
    if (!selectedBasket) return;
    try {
      setPresentationLoading(true);
      const presentationsData = await referenceDataSyncService.getPresentationsForFilters(
        selectedBasket?.id
      );

      setPresentations(presentationsData);
    } catch (error) {
      console.error('Failed to load presentations from IndexedDB:', error);
    } finally {
      setPresentationLoading(false);
    }
  }, [selectedBasket]);

  const fetchCityData = useCallback(async () => {
    if (!selectedBasket) return;
    try {
      setPriceLoading(true);

      const now = Date.now();
      let startTime: number;

      switch (selectedTimeframe) {
        case '1d':
          startTime = now - 24 * 60 * 60 * 1000;
          break;
        case '7d':
          startTime = now - 7 * 24 * 60 * 60 * 1000;
          break;
        case '30d':
          startTime = now - 30 * 24 * 60 * 60 * 1000;
          break;
        case '90d':
          startTime = now - 90 * 24 * 60 * 60 * 1000;
          break;
        default:
          startTime = now - 7 * 24 * 60 * 60 * 1000;
      }

      const queryParams: Record<string, string | string[]> = {
        startTime: startTime.toString(),
        endTime: now.toString(),
        basketId: selectedBasket.id.toString(),
      };

      if (selectedPresentation) {
        queryParams.productIds = [selectedPresentation.id.toString()];
      }

      const response = await fetcher.get(
        `${API_ENDPOINTS.PRICE.GET_ALL_PUBLIC_PRICES_BY}/city`,
        queryParams,
        {},
        ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
      );
      const payload = unwrapDataResponse<
        RegionalCityPrice[] | { data?: RegionalCityPrice[]; averages?: { national?: number } }
      >(response.data);
      const dataList = (Array.isArray(payload) ? payload : payload?.data || []).map(
        normalizeRegionalPrice
      );
      const averages = Array.isArray(payload) ? undefined : payload?.averages;

      if (!dataList || dataList.length === 0) {
        setRegionalPrice(null);
        setMaxRegionalPrice(0);
        setNationalAveragePrice(null);
        setDepartmentAveragePrice(null);
        setMunicipalityAveragePrice(null);
        return;
      }

      setRegionalPrice(dataList);

      const prices = dataList.map((item: RegionalCityPrice) => {
        const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
        return isNaN(price) ? 0 : price;
      });
      const validPrices = prices.filter((price: number) => price > 0);
      setMaxRegionalPrice(Math.max(...validPrices, 0));

      const nationalAverage =
        averages?.national ||
        (validPrices.length > 0
          ? validPrices.reduce((sum: number, price: number) => sum + price, 0) / validPrices.length
          : 0);
      setNationalAveragePrice(nationalAverage);

      if (userLocation && userLocation.municipality) {
        const municipalityPrices = dataList
          .filter((item: RegionalCityPrice) => item.locationId === userLocation.locationId)
          .map((item: RegionalCityPrice) => {
            const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
            return isNaN(price) ? 0 : price;
          })
          .filter((price: number) => price > 0);

        const municipalityAverage =
          municipalityPrices.length > 0
            ? municipalityPrices.reduce((sum: number, price: number) => sum + price, 0) /
              municipalityPrices.length
            : 0;

        setMunicipalityAveragePrice(municipalityAverage);
      } else {
        setMunicipalityAveragePrice(null);
      }

      if (userLocation && userLocation.locationId) {
        const userPriceData = dataList.find(
          (item: RegionalCityPrice) => item.locationId === userLocation.locationId
        );
        if (userPriceData) {
          // User price data is available but not currently used
          // const userPrice =
          //   typeof userPriceData.price === 'string'
          //     ? parseFloat(userPriceData.price)
          //     : userPriceData.price;
        }
      }
    } catch (error) {
      console.error('Failed to fetch city price data:', error);
    } finally {
      setPriceLoading(false);
    }
  }, [selectedBasket, selectedPresentation, userLocation, selectedTimeframe]);

  const fetchDistrictData = useCallback(async () => {
    if (!selectedBasket) return;
    try {
      setPriceLoading(true);

      const now = Date.now();
      let startTime: number;

      switch (selectedTimeframe) {
        case '1d':
          startTime = now - 24 * 60 * 60 * 1000;
          break;
        case '7d':
          startTime = now - 7 * 24 * 60 * 60 * 1000;
          break;
        case '30d':
          startTime = now - 30 * 24 * 60 * 60 * 1000;
          break;
        case '90d':
          startTime = now - 90 * 24 * 60 * 60 * 1000;
          break;
        default:
          startTime = now - 7 * 24 * 60 * 60 * 1000;
      }

      const queryParams: Record<string, string | string[]> = {
        startTime: startTime.toString(),
        endTime: now.toString(),
        basketId: selectedBasket.id.toString(),
      };

      if (selectedPresentation) {
        queryParams.productIds = [selectedPresentation.id.toString()];
      }

      const response = await fetcher.get(
        `${API_ENDPOINTS.PRICE.GET_ALL_PUBLIC_PRICES_BY}/district`,
        queryParams,
        {},
        ENVIRONMENT_CONFIG.ANALYSIS_SERVICE
      );
      const payload = unwrapDataResponse<
        RegionalDistrictPrice[] | { data?: RegionalDistrictPrice[] }
      >(response.data);
      const dataList = (Array.isArray(payload) ? payload : payload?.data || []).map(
        normalizeRegionalPrice
      );

      if (!dataList.length) {
        setRegionalDistrictPrice(null);
        setMaxRegionalDistrictPrice(0);
        setDepartmentAveragePrice(null);
        return;
      }

      setRegionalDistrictPrice(dataList);

      const prices = dataList.map((item: RegionalDistrictPrice) =>
        typeof item.price === 'string' ? parseFloat(item.price) : item.price
      );
      setMaxRegionalDistrictPrice(Math.max(...prices, 0));

      // Calculate department average from district data
      if (userLocation && userLocation.department) {
        const departmentPrices = dataList
          .filter((item: RegionalDistrictPrice) => item.district === userLocation.department)
          .map((item: RegionalDistrictPrice) => {
            const price = typeof item.price === 'string' ? parseFloat(item.price) : item.price;
            return isNaN(price) ? 0 : price;
          })
          .filter((price: number) => price > 0);

        const departmentAverage =
          departmentPrices.length > 0
            ? departmentPrices.reduce((sum: number, price: number) => sum + price, 0) /
              departmentPrices.length
            : 0;
        setDepartmentAveragePrice(departmentAverage);
      }
    } catch (error) {
      console.error('Failed to fetch district price data:', error);
    } finally {
      setPriceLoading(false);
    }
  }, [selectedBasket, selectedPresentation, selectedTimeframe, userLocation]);

  const districtStyleFunction = useCallback(
    (feature: DistrictLocationFeature | undefined) => {
      const district = normalizeString(feature?.properties.district || '');

      const price = regionalDistrictPrice?.find(
        (item) => normalizeString(item.district) === district
      )?.price;

      const isUserRegion = userLocation && normalizeString(userLocation.department) === district;

      return {
        fillColor: isUserRegion
          ? '#ff6b35' // Orange for user's region
          : price !== undefined && price !== null && price > 0
            ? getColorByIntensity(price, maxRegionalDistrictPrice)
            : '#e5e7eb', // Light grey for no data
        fillOpacity: isUserRegion ? 0.8 : 0.6,
        color: isUserRegion ? '#fff' : '#fff',
        weight: isUserRegion ? 3 : 1,
      };
    },
    [regionalDistrictPrice, maxRegionalDistrictPrice, userLocation]
  );

  const cityStyleFunction = useCallback(
    (feature: CityLocationFeature | undefined) => {
      const locationId = feature?.properties.locationId;
      const price = regionalPrice?.find((item) => item.locationId === locationId)?.price;
      const isUserRegion = userLocation && userLocation.locationId === locationId;

      return {
        fillColor: isUserRegion
          ? '#ff6b35' // Orange for user's region (consistent with other maps)
          : price !== undefined && price !== null && price > 0
            ? getColorByIntensity(price, maxRegionalPrice)
            : '#e5e7eb', // Light grey for no data
        fillOpacity: isUserRegion ? 0.8 : 0.6,
        color: isUserRegion ? '#fff' : '#fff',
        weight: isUserRegion ? 3 : 1,
      };
    },
    [regionalPrice, maxRegionalPrice, userLocation]
  );

  const popupContentFunction = useCallback(
    (
      feature: CityLocationFeature | DistrictLocationFeature,
      _department: unknown,
      _municipality: unknown,
      usersLocation: { locationId: number; department: string | undefined }
    ): string => {
      const featureProperties = feature?.properties;
      if (featureProperties && 'city' in featureProperties) {
        const props = featureProperties as CityLocationProperties;
        const item = regionalPrice?.find((item) => item?.locationId === props?.locationId);
        const isUserRegion = usersLocation && usersLocation?.locationId === props?.locationId;

        return `
          <div>
            <h3 class="text-lg font-semibold ${isUserRegion ? 'text-orange-600' : ''}">
              ${item?.city || props?.city} ${isUserRegion ? '(Your Region)' : ''}
            </h3>
            <p><strong>${t('averagePriceLabel')}:</strong> L. ${item?.price !== undefined ? (typeof item.price === 'string' ? parseFloat(item?.price).toFixed(2) : item?.price.toFixed(2)) : 'N/A'}</p>
            <p><strong>${t('inspections')}:</strong> ${item?.inspectionCount || 0}</p>
            <p><strong>${t('establishments')}:</strong> ${item?.establishmentCount || 0}</p>
          </div>
        `;
      } else {
        const props = featureProperties as DistrictLocationProperties;

        const item = regionalDistrictPrice?.find(
          (item) => normalizeString(item?.district) === normalizeString(props?.district)
        );
        const isUserRegion =
          usersLocation &&
          normalizeString(usersLocation?.department) === normalizeString(props?.district);

        return `
          <div>
            <h3 class="text-lg font-semibold ${isUserRegion ? 'text-orange-600' : ''}">
              ${item?.district || props?.district} ${isUserRegion ? '(Your Region)' : ''}
            </h3>
            <p><strong>${t('averagePriceLabel')}:</strong> L. ${item?.price !== undefined ? (typeof item?.price === 'string' ? parseFloat(item?.price).toFixed(2) : item?.price.toFixed(2)) : 'N/A'}</p>
            <p><strong>${t('inspections')}:</strong> ${item?.inspectionCount || 0}</p>
            <p><strong>${t('establishments')}:</strong> ${item?.establishmentCount || 0}</p>
          </div>
        `;
      }
    },
    [regionalPrice, regionalDistrictPrice, t]
  );

  useEffect(() => {
    fetchBasketsData();
    fetchLocationsData();
  }, [fetchBasketsData, fetchLocationsData]);

  // Set default municipality when locations are loaded
  useEffect(() => {
    if (locations.length > 0 && selectedDepartment === Configuration.defaultLocation.department) {
      const filteredMunicipalities = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.city);

      if (filteredMunicipalities.includes(Configuration.defaultLocation.municipality)) {
        setSelectedMunicipality(Configuration.defaultLocation.municipality);
      }
    }
  }, [locations, selectedDepartment]);

  useEffect(() => {
    if (selectedDepartment) {
      const filteredMunicipalities = locations
        .filter((location) => location.district === selectedDepartment)
        .map((location) => location.city);
      setMunicipalities(filteredMunicipalities);

      setSelectedMunicipality((prevMunicipality) => {
        if (!filteredMunicipalities.includes(prevMunicipality)) {
          if (filteredMunicipalities.includes(Configuration.defaultLocation.municipality)) {
            return Configuration.defaultLocation.municipality;
          } else {
            return '';
          }
        }
        return prevMunicipality;
      });
    } else {
      setMunicipalities([]);
      setSelectedMunicipality('');
    }
  }, [selectedDepartment, locations]);

  useEffect(() => {
    if (selectedDepartment && selectedMunicipality) {
      const location = locations.find(
        (loc) => loc.district === selectedDepartment && loc.city === selectedMunicipality
      );
      if (location) {
        setUserLocation({
          department: selectedDepartment,
          municipality: selectedMunicipality,
          locationId: location.id,
        });
      }
    } else if (selectedDepartment && !selectedMunicipality) {
      setUserLocation({
        department: selectedDepartment,
        municipality: '',
        locationId: undefined,
      });
    } else {
      setUserLocation(null);
    }
  }, [selectedDepartment, selectedMunicipality, locations]);

  useEffect(() => {
    setSelectedPresentation(undefined);
    fetchPresentationsData();
  }, [selectedBasket, fetchPresentationsData]);

  useEffect(() => {
    setRegionalPrice(null);
    setRegionalDistrictPrice(null);
    setDepartmentAveragePrice(null);
    setMunicipalityAveragePrice(null);
    setNationalAveragePrice(null);
    setMaxRegionalPrice(0);
    setMaxRegionalDistrictPrice(0);
  }, [selectedPresentation]);

  useEffect(() => {
    if (selectedBasket && userLocation) {
      fetchCityData();
      fetchDistrictData();
    }
  }, [selectedBasket, selectedPresentation, userLocation, fetchCityData, fetchDistrictData]);

  return (
    <div className="section user-region-comparison mx-10" ref={ref}>
      <h3 className="mb-4 text-xl font-semibold text-gray-800">{t('regionalPrices')}</h3>

      {/* Filters */}
      <div className="mb-6">
        <h3 className="mb-4 text-lg font-semibold">{t('filters')}</h3>
        <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5">
          <Select
            className="w-full [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            placeholder={t('selectDepartment')}
            allowClear
            onClear={() => setSelectedDepartment(Configuration.defaultLocation.department)}
            value={selectedDepartment || Configuration.defaultLocation.department}
            onChange={(value) => {
              if (value !== undefined) {
                setSelectedDepartment(value);
              }
            }}
            options={departments.map((dept) => ({
              label: dept,
              value: dept,
            }))}
          />
          <Select
            className="w-full [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            placeholder={t('selectMunicipality')}
            allowClear
            value={selectedMunicipality || undefined}
            onChange={setSelectedMunicipality}
            disabled={!selectedDepartment || municipalities.length === 0}
            options={municipalities.map((muni) => ({
              label: muni,
              value: muni,
            }))}
          />
          <Select
            className="w-full [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            placeholder={t('filter_basket')}
            allowClear
            value={selectedBasket?.id || undefined}
            onChange={(value) => {
              const basket = baskets.find((b) => b.id === value);
              setSelectedBasket(basket);
            }}
            loading={basketLoading}
            options={baskets.map((basket) => ({
              label: basket.name,
              value: basket.id,
            }))}
          />
          <Select
            className="w-full [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            placeholder={t('filter_product')}
            allowClear
            value={selectedPresentation?.id || undefined}
            onChange={(value) => {
              const presentation = presentations.find((p) => p.id === value);
              setSelectedPresentation(presentation);
            }}
            loading={presentationLoading}
            disabled={!selectedBasket}
            options={presentations.map((presentation) => ({
              label: presentation.name,
              value: presentation.id,
            }))}
          />
          <Select
            className="w-full [&>.ant-select-selector]:!rounded-full [&_.ant-select-selection-item]:!text-sm [&_.ant-select-selection-placeholder]:!text-sm [&_.ant-select-selection-placeholder]:!text-black"
            placeholder="Timeframe"
            value={selectedTimeframe}
            onChange={setSelectedTimeframe}
            options={timeframes}
          />
        </div>
      </div>

      {/* Price Comparison Stats */}
      {selectedPresentation && userLocation && (
        <div className="mb-6">
          <Row gutter={16}>
            <Col span={8}>
              <Card className="rounded-lg border border-gray-200 shadow-sm">
                <Statistic
                  title={`${userLocation?.department || 'Department'} Average Price`}
                  value={departmentAveragePrice || 'N/A'}
                  prefix={departmentAveragePrice ? 'L.' : ''}
                  valueStyle={{ color: departmentAveragePrice ? '#ff6b35' : '#999' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card className="rounded-lg border border-gray-200 shadow-sm">
                <Statistic
                  title={`${userLocation?.municipality || 'Municipality'} Average Price`}
                  value={municipalityAveragePrice || 'N/A'}
                  prefix={municipalityAveragePrice ? 'L.' : ''}
                  valueStyle={{ color: municipalityAveragePrice ? '#1890ff' : '#999' }}
                />
              </Card>
            </Col>
            <Col span={8}>
              <Card className="rounded-lg border border-gray-200 shadow-sm">
                <Statistic
                  title="National Average Price"
                  value={nationalAveragePrice || 'N/A'}
                  prefix={nationalAveragePrice ? 'L.' : ''}
                  valueStyle={{ color: nationalAveragePrice ? '#52c41a' : '#999' }}
                />
              </Card>
            </Col>
          </Row>
        </div>
      )}

      <div>
        <p>{t('noteRegardingMap')}</p>
      </div>
      {/* Map */}
      <div
        className="map-container relative flex h-auto items-center justify-center text-6xl text-primary-gray-300"
        id="user-region-map"
      >
        <Suspense fallback={<SpinLoader type={SpinTypes.CENTER} />}>
          <ConsumerMap
            key={`${selectedBasket?.id || 'default'}-${selectedPresentation?.refId || 'default'}-${maxRegionalPrice}-${maxRegionalDistrictPrice}-${selectedTimeframe}`}
            districtStyleFunction={districtStyleFunction}
            cityStyleFunction={cityStyleFunction}
            districtBorderStyleFunction={districtBorderStyleFunction}
            popupContentFunction={popupContentFunction}
            department={selectedDepartment}
            municipality={selectedMunicipality}
            userLocation={userLocation}
          />
        </Suspense>
        {priceLoading && (
          <div className="absolute inset-0 flex items-center justify-center bg-white bg-opacity-75">
            <SpinLoader type={SpinTypes.CENTER} />
          </div>
        )}
      </div>

      {/* Legend */}
      {selectedPresentation && userLocation && (
        <div className="mt-4 flex justify-center">
          <div className="flex items-center gap-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-orange-500"></div>
              <span>{t('yourRegion')}</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="h-4 w-4 rounded bg-blue-500"></div>
              <span>{t('otherRegions')}</span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
});

export default UserRegionComparison;
